import { useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import ChargerType from "../../types/ChargerType"
import Comm from "../../Comm"
import styled from "styled-components"
import PageTitle from "../../components/PageTitle"
import WhiteSpace from "../../components/Whitespace"
import DateRangePickerWithPresets from "../../components/DateRangePickerWithPresets"
import { useTranslation } from "react-i18next"
import LogType from "../../types/LogType"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import Navigator from "../../components/Navigator"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import Icon from "../../Icon"
import ExportButton from "../../modules/ExportButton"
import { useNotification } from "../../modules/Notification"
import Switch from "../../components/Switch"
import LoaderDots from "../../components/LoaderDots"
import { getLogCodeType, getLogCodeLabel } from "../../utils/logCodes"
import Modal from "../../components/Modal"
import InputWithLabel from "../../components/InputWithLabel"
import Button from "../../components/Button"
import normalizeString from "../../utils/normalizeString"
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"

const LogToolbar = styled.div`
  position: absolute;
  right: 12px;
  top: 18px;
  z-index: 1;
  display: flex;
  gap: 14px;

  /* Each icon carries a 44px tap expander. At a 14px gap the two expanders overlap, so a tap near
     either edge lands on the wrong one - they have to sit a full tap width apart. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    gap: ${TAP_MIN - 16}px;
  }
`

const DateRangePickerContainer = styled.div`
  position: absolute;
  margin-left: 25vw;
  margin-top: 15px;
  z-index: 1;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    margin-left: 0px;
  }
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

const LogCode = styled.p<{ $code: string }>` // normal / warning / error
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.$code == 'normal' ? props.theme.colors.black : props.theme.colors.white};
  border-radius: 40px;
  //width: 70px;
  padding-left: 12px;
  padding-right: 12px;
  line-height: 22px;
  text-align: center;
  margin: 0px;
  margin-left: -8px;
  margin-right: 24px;
  background-color: ${props => {
    switch (props.$code) {
      case 'normal':
        return props.theme.colors.gray_200
      case 'warning':
        return props.theme.colors.warning
      case 'error':
        return props.theme.colors.danger
    }
  }};
`

const LogIndicator = styled.div<{ $code: string }>` // normal / warning / error
  width: 3px;
  height: 28px;
  margin-top: 2px;
  margin-bottom: 2px;
  border-radius: 3px;
  background-color: ${props => {
    switch (props.$code) {
      case 'normal':
        return props.theme.colors.primary
      case 'warning':
        return props.theme.colors.warning
      case 'error':
        return props.theme.colors.danger
    }
  }};
`

const ChargerDetailsTable = styled.table`
  width: calc(100%);
  background-color: ${props => props.theme.colors.gray_300};
  border-radius: 16px;

  /* The row below becomes a grid on small widths, which takes it out of the table layout algorithm.
     Left as a table, the table then has no cells to size itself from and collapses to one character
     per column. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: block;

    tbody {
      display: block;
    }
  }
`

const ChargerDetailsTitle = styled.p`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  overflow-wrap: anywhere;
  margin-bottom: 4px;
  margin-top: 20px;
`

const ChargerDetailsValue = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  overflow-wrap: anywhere;
`

const ChargerDetailsRow = styled.tr`
  display: flex;

  /* The percentage cells add up to 90% but each one needs its icon plus a full charger id or email,
     so below ~1300px they stop shrinking and print on top of each other. A grid with a real column
     minimum lays them out two-up instead; a wrapping flex row let them shrink until the labels broke
     one character per line. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    align-items: start;
    gap: 12px 20px;
    padding: 16px;

    > td {
      display: flex;
      /* The cell widths are inline percentages, so nothing but !important can release them - and
         20% of a 157px grid track is 31px, which is what broke the labels one character per line. */
      width: auto !important;
      min-width: 0;
    }

    > td > div {
      min-width: 0;
    }
  }
`

const ChargerDetailsGoToChargerContainer = styled.div`
  position: absolute;
  right: 0px;
  display: flex;
  margin-right: 16px;
  cursor: pointer;

  /* right:0 of the strip lands this link on top of the last cells once the row wraps. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    right: auto;
    margin-right: 0px;
    align-items: center;
  }
`

const ChargerDetailsGoToChargerLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.primary};
  margin: 0px;
  margin-top: 30px;
`

const ToolbarButtonContainer = styled.div`
  position: absolute;
  top: 18px;
  right: 26px;
  display: flex;
  /* Absolutely positioned toolbars cannot wrap, so on a phone the button row overlays the page
     title and its leading buttons land at negative x, which no amount of panning can reach.
     Put it back in flow and let it wrap. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    right: auto;
    left: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
`

const LineFilterModalContainer = styled.div`
  padding: 24px;
  width: 280px;
`

const LineFilterModalTitle = styled.p`
  font-weight: 700;
  font-size: 16px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-bottom: 20px;
`

const LineFilterModalButtonContainer = styled.div`
  display: flex;
  margin-top: 24px;
`

export default function ChargerLogs() {
  const { chargerId } = useParams()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { addNotification } = useNotification()

  const [charger, setCharger] = useState<ChargerType>()
  const [logs, setLogs] = useState<LogType[]>([])
  const [isLoadingLogs, setIsLoadingLogs] = useState<boolean>(true)
  const [lastRefresh, setLastRefresh] = useState<string>('')
  const [selectedDateRange, setSelectedDateRange] = useState<Date[]>()
  const [logVersion, setLogVersion] = useState<"V1" | "V2">()
  const logVersionRef = useRef<"V1" | "V2" | undefined>(undefined)
  const [highlightLineNumber, setHighlightLineNumber] = useState<string | null>(searchParams.get('lineNumber'))

  // Line number range filter (VLT-1990)
  const [isShowingLineFilter, setIsShowingLineFilter] = useState<boolean>(false)
  const [lineFilterFromInput, setLineFilterFromInput] = useState<string>('')
  const [lineFilterToInput, setLineFilterToInput] = useState<string>('')
  const [appliedLineFilter, setAppliedLineFilter] = useState<{ from: number, to: number } | null>(null)

  // Mirror of the table's search box, so copy/export reflect the active search filter (VLT-1904)
  const [tableSearchTerm, setTableSearchTerm] = useState<string>('')

  // The logs actually rendered: the fetched logs narrowed down by the active line number filter
  const displayedLogs = appliedLineFilter
    ? logs.filter(log => {
      const n = parseInt(log.lineNumber)
      return !isNaN(n) && n >= appliedLineFilter.from && n <= appliedLineFilter.to
    })
    : logs

  // The logs that copy / export operate on: the displayed logs further narrowed by the table's search.
  // The table only visually filters once the term is longer than one character, so we mirror that here.
  // The matched fields mirror the cells the table searches (line number, date, time, app timestamp, message).
  const exportLogs = tableSearchTerm.length > 1
    ? displayedLogs.filter(log => normalizeString([
      log.lineNumber,
      log.day.replace(/-/g, '.'),
      log.time,
      (log.appTs || '-').toString(),
      log.message.replace(/,/g, ', ')
    ].join(' ')).includes(normalizeString(tableSearchTerm)))
    : displayedLogs

  function handleSelectLogLine(lineNumber: string, day: string) {
    setHighlightLineNumber(lineNumber)
    searchParams.set('lineNumber', lineNumber)
    // Persist the log's day so the deep-link still resolves on later visits, when this day
    // is no longer the most recent available log day (VLT-2002)
    if (day) searchParams.set('dateFilter', day)
    setSearchParams(searchParams)
    // Clear previous highlights
    document.querySelectorAll('tr[data-highlighted]').forEach(row => {
      (row as HTMLElement).style.backgroundColor = ''
      row.removeAttribute('data-highlighted')
    })
    // Highlight selected row
    const element = document.getElementById(`log-line-${lineNumber}`)
    if (element) {
      const row = element.closest('tr')
      if (row) {
        row.style.backgroundColor = '#fffad1'
        row.setAttribute('data-highlighted', 'true')
      }
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (highlightLineNumber && logs.length > 0) {
      setTimeout(() => {
        const element = document.getElementById(`log-line-${highlightLineNumber}`)
        if (element) {
          const row = element.closest('tr')
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' })
            row.style.backgroundColor = '#fffad1'
            row.setAttribute('data-highlighted', 'true')
          }
        }
      }, 300)
    }
  }, [logs])

  async function loadData(resetParams?: boolean) {
    setIsLoadingLogs(true)
    let reachedFetchLogs = false
    try {
      const charger = await fetchCharger()
      const availableLogDays = await fetchAvailableLogDays()

      if (charger && availableLogDays) {
        // Read the dateFilter from search params if exists, use the default date range (only today) if does not exist
        const dateFilterSearchParam = resetParams ? null : searchParams.get('dateFilter')
        const defaultDateRange = dateFilterSearchParam ? dateFilterSearchParam.split('_').map(s => new Date(s)) : [new Date(availableLogDays[0])]
        setSelectedDateRange(defaultDateRange)
        setCharger({ ...charger, availableLogDays: availableLogDays })
        reachedFetchLogs = true
        // fetchLogs has its own finally that releases the loader; dont release here or it would flip off then on
        await fetchLogs({ ...charger, availableLogDays: availableLogDays }, defaultDateRange)

      } else {
        console.log('Could not fetch charger or available log days')
      }
    } finally {
      // Only release the loader if fetchLogs never ran (it owns the loader once it starts)
      if (!reachedFetchLogs) setIsLoadingLogs(false)
    }
  }

  async function fetchCharger() {
    const response = await Comm('/api-charger/charger', {
      headers: { "Charger_id": chargerId, "Content-Type": "application/json" }
    })
    return await response.json()
  }

  // LOG V1
  /*async function fetchAvailableLogDays() {
    // TODO: Replace with endpoint that gives back only the neccesery chargers available days
    const response = await Comm('/api-log/charger/log/frontend/available', {})
    const result: { chargerId: string, availableDays: string[] }[] = await response.json()

    for (const charger of result) {
      if (charger.chargerId == chargerId) {
        return charger.availableDays
      }
    }
    // If no days are available, show todays date insted of throwing error
    return [new Date().toISOString().slice(0, 10)]
  }*/

  // LOG V2
  async function fetchAvailableLogDays() {
    const response = await Comm('/api-log/v2/charger/log/frontend/available', { headers: { "Charger_id": chargerId } })
    const result: { chargerId: string, availableDays: string[] }[] = await response.json()

    for (const charger of result) {
      if (charger.chargerId == chargerId) {

        if (logVersionRef.current === "V1") {
          return await fetchAvailableLogDaysFallbackToV1()
        } else if (logVersionRef.current === "V2") {
          // User pinned V2: if V2 has no days, dont fall back, just show today
          return charger.availableDays.length > 0 ? charger.availableDays.reverse() : [new Date().toISOString().slice(0, 10)]
        } else {

          // If used logVerison is not set, decide if V2 is available and fallback to V1 if not
          // If there is only a single day and its 02.01, than also fall back to V1
          if (charger.availableDays.length === 0 || (charger.availableDays.length === 1 && charger.availableDays[0] === "2026-02-01")) {
            // Set logs toggle to V1 so its clear that were using V1
            logVersionRef.current = "V1"
            setLogVersion("V1")
            return await fetchAvailableLogDaysFallbackToV1()
          } else {
            // Set logs toggle to V2 so its clear that were using V2
            logVersionRef.current = "V2"
            setLogVersion("V2")
            return charger.availableDays.reverse()
          }
        }
      }
    }
    // If no days are available, show todays date insted of throwing error
    return [new Date().toISOString().slice(0, 10)]
  }

  // FALLBACK TO V1
  async function fetchAvailableLogDaysFallbackToV1() {
    const response = await Comm('/api-log/charger/log/frontend/available', { headers: { "Charger_id": chargerId } })
    const result: { chargerId: string, availableDays: string[] }[] = await response.json()

    for (const charger of result) {
      if (charger.chargerId == chargerId) {
        // If V1 has no days for this charger, show today instead of returning [] (which crashes new Date())
        return charger.availableDays.length > 0 ? charger.availableDays.reverse() : [new Date().toISOString().slice(0, 10)]
      }
    }
    // If no days are available, show todays date insted of throwing error
    return [new Date().toISOString().slice(0, 10)]
  }

  // LOG V1
  /*async function fetchLogs(charger: ChargerType, dateRange: Date[]) {
    const daysToFetch: string[] = []
    if (dateRange.length == 1) {
      daysToFetch.push(dateRange[0].toISOString().split('T')[0])
    } else {
      const endDate = new Date(dateRange[1].getTime())
      endDate.setDate(endDate.getDate() + 1)
      for (const date = new Date(dateRange[0].getTime()); date <= endDate; date.setDate(date.getDate() + 1)) {
        daysToFetch.push(new Date(date).toISOString().split('T')[0])
      }
    }

    const allLogsForSelectedDateRange: LogType[] = []
    for (const day of daysToFetch) {
      if (charger.availableLogDays.includes(day)) {
        const allLogsForDay = await Comm(`/api-log/charger/log/frontend?day=${day}`, { headers: { "Charger_id": chargerId } })
        const allLogsForDayResult: LogType[] = await allLogsForDay.json()
        allLogsForDayResult.map(log => log.date = day)
        allLogsForSelectedDateRange.push(...allLogsForDayResult)
      }
    }
    allLogsForSelectedDateRange.reverse()
    setLogs(allLogsForSelectedDateRange)
    setLastRefresh(new Date().toString().split(' ')[4])
  }*/

  // LOG V2
  async function fetchLogs(charger: ChargerType, dateRange: Date[]) {
    try {
      const daysToFetch: string[] = []
      if (dateRange.length == 1) {
        daysToFetch.push(dateRange[0].toISOString().split('T')[0])
      } else {
        const endDate = new Date(dateRange[1].getTime())
        endDate.setDate(endDate.getDate() + 1)
        for (const date = new Date(dateRange[0].getTime()); date <= endDate; date.setDate(date.getDate() + 1)) {
          daysToFetch.push(new Date(date).toISOString().split('T')[0])
        }
      }

      const allLogsForSelectedDateRange: LogType[] = []
      for (const day of daysToFetch) {
        if (charger.availableLogDays.includes(day)) {
          // ONLY SILENT: TRUE, WHILE V1 FALLBACK IS ACTIVE
          const allLogsForDay = await Comm(`/api-log/v2/charger/log/frontend?day=${day}&pageSize=1000000000`, { headers: { "Charger_id": chargerId } }, true)

          // FALLBACK TO V1
          let allLogsForDayResult: LogType[] = []

          if (logVersionRef.current === "V1") {
            allLogsForDayResult = await fetchLogsForDayFallbackToV1(charger, day) as unknown as LogType[]
          } else if (logVersionRef.current === "V2") {
            if (allLogsForDay.ok) {
              const v2Result = await allLogsForDay.json()
              const parsed = v2Result?.data ?? v2Result
              allLogsForDayResult = Array.isArray(parsed) ? parsed : []
            }
          } else {

            // If used logVerison is not set, decide if V2 is available and fallback to V1 if not
            if (allLogsForDay.status != 404) {
              const v2Result = await allLogsForDay.json()
              const parsed = v2Result?.data ?? v2Result
              allLogsForDayResult = Array.isArray(parsed) ? parsed : []
              // Set logs toggle to V2 so its clear that were using V2
              logVersionRef.current = "V2"
              setLogVersion("V2")
            } else {
              allLogsForDayResult = await fetchLogsForDayFallbackToV1(charger, day) as unknown as LogType[]
              // Set logs toggle to V1 so its clear that were using V1
              logVersionRef.current = "V1"
              setLogVersion("V1")
            }
          }

          // Filter out non OK logs
          allLogsForDayResult = allLogsForDayResult.filter(log => log.status === "OK")
          allLogsForSelectedDateRange.push(...allLogsForDayResult)
        }
      }
      allLogsForSelectedDateRange.reverse()
      setLogs(allLogsForSelectedDateRange)
      setLastRefresh(new Date().toString().split(' ')[4])
    } finally {
      // Always release the loader so the empty-state label can render
      setIsLoadingLogs(false)
    }
  }

  // FALLBACK TO V1
  async function fetchLogsForDayFallbackToV1(charger: ChargerType, day: string) {
    if (charger.availableLogDays.includes(day)) {
      // Silent: 404 here just means there are no V1 logs for this day, return empty
      const allLogsForDay = await Comm(`/api-log/charger/log/frontend?day=${day}`, { headers: { "Charger_id": chargerId } }, true)
      if (!allLogsForDay.ok) return []
      const allLogsForDayResult = await allLogsForDay.json()

      // Translate V1 logs to V2
      const translated = []
      for (const log of allLogsForDayResult) {
        translated.push({
          _id: 0,
          chargerId: 0,
          timestamp: 0,
          day: day,
          time: log.day,
          lineNumber: log.lineNumber,
          logCode: parseInt(log.type.split(" ")[1]),
          appTs: 0,
          message: log.log,
          status: "OK",
          rawLine: '',
          error: '',
          createdAt: 0
        })
      }
      return translated
    }
    return []
  }


  async function handelRefresh() {
    if (charger && selectedDateRange) {
      const hasParams = searchParams.toString().length > 0
      setLogs([])
      setHighlightLineNumber(null)
      setSearchParams({}, { replace: true })
      await loadData(hasParams)
    }
  }

  function handleChooseDateRange(range: Date[]) {
    if (charger) {

      // Set the selected range to dateFilter search param
      searchParams.set('dateFilter', range.length == 2 ? range[0].toISOString().split('T')[0] + '_' + range[1].toISOString().split('T')[0] : range[0].toISOString().split('T')[0])
      setSearchParams(searchParams)

      setSelectedDateRange(range)
      setLogs([])
      setIsLoadingLogs(true)
      fetchLogs(charger, range)
    }
  }

  async function handleChargerUpdateLog() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 3 })
    })

    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleChargerSoftReset() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 12 })
    })

    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleChargerHardReset() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 10 })
    })

    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function handleChargerUpdate() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 15, "sub_cmd": 1 })
    })

    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }


  function openLineFilter() {
    // Offer the currently selected/focused line as the start, and start + 100 as the end (VLT-1990).
    // Fall back to the first displayed log line when nothing is selected yet.
    const defaultFrom = highlightLineNumber || displayedLogs[0]?.lineNumber || ''
    const defaultFromNum = parseInt(defaultFrom)
    setLineFilterFromInput(defaultFrom)
    setLineFilterToInput(isNaN(defaultFromNum) ? '' : String(defaultFromNum + 100))
    setIsShowingLineFilter(true)
  }

  function applyLineFilter() {
    const from = parseInt(lineFilterFromInput)
    const to = parseInt(lineFilterToInput)
    // If neither bound is a valid number, treat it as clearing the filter
    if (isNaN(from) && isNaN(to)) {
      setAppliedLineFilter(null)
    } else {
      const lower = isNaN(from) ? -Infinity : from
      const upper = isNaN(to) ? Infinity : to
      // Be forgiving if the user enters the bounds in reverse order
      setAppliedLineFilter({ from: Math.min(lower, upper), to: Math.max(lower, upper) })
    }
    setIsShowingLineFilter(false)
  }

  function clearLineFilter() {
    setAppliedLineFilter(null)
    setLineFilterFromInput('')
    setLineFilterToInput('')
  }

  function handleCopyLogsToClipboard() {
    if (!exportLogs || exportLogs.length == 0) return
    const padCenter = (str: string, len: number) => {
      const pad = len - str.length
      if (pad <= 0) return str
      const left = Math.floor(pad / 2)
      return ' '.repeat(left) + str + ' '.repeat(pad - left)
    }
    const lines = exportLogs.map(log => {
      const label = getLogCodeLabel(log.logCode)
      const appTsStr = log.appTs ? String(log.appTs) : '-'
      const appTsPadded = appTsStr.includes('.') ? appTsStr.padEnd(appTsStr.indexOf('.') + 3) : appTsStr + '   '
      return `${log.lineNumber} | ${log.day.replace(/-/g, '.')} | ${log.time} | ${appTsPadded.padStart(10)} | ${padCenter(label, 10)} | ${log.message}`
    })
    navigator.clipboard.writeText(lines.join('\n'))
    addNotification('Copied to clipboard', 'TEXT')
  }

  return (
    <>
      <PageTitle label={t('chargerLogs_chargerLOGs')} />
      <ToolbarButtonContainer>
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('charger_refresh')} onClick={handelRefresh} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.export" iconColor="white" label={t('chargerLogs_uploadLog')} onClick={handleChargerUpdateLog} backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.refresh" iconColor="white" label={t('chargerLogs_softReset')} onClick={handleChargerSoftReset} backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.refresh" iconColor="white" label={t('chargerLogs_hardReset')} onClick={handleChargerHardReset} backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          <ButtonWithIcon icon="general.wrench" iconColor="white" label={t('chargerLogs_update')} onClick={handleChargerUpdate} backgroundColor="dark" />
        </div>
        <WhiteSpace width="8px" />
        <div>
          {logs && <ExportButton fileName="Logs" titleRow={[
            { label: 'Line', width: '10' },
            { label: 'Type', width: '10' },
            { label: 'Date', width: '20' },
            { label: 'Message', width: '150' },
          ]} data={exportLogs.map(log => [log.lineNumber, `Code: ${log.logCode}`, `${log.day.replace(/-/g, '.')} ${log.time}`, log.message.replace(/,/g, ', ')])} />
          }
        </div>
        <WhiteSpace width="16px" />
        <div style={{ display: 'flex' }}>
          <p style={{ marginTop: "8px", fontWeight: '700', fontSize: "13px", marginLeft: "8px", marginRight: "8px" }}>V1</p>
          <Switch toggled={logVersion === "V2"} setToggled={value => {
            if (value) {
              logVersionRef.current = "V2"
              setLogVersion("V2")
            } else {
              logVersionRef.current = "V1"
              setLogVersion("V1")
            }
            console.log(value)
            setLogs([])
            loadData()
          }} />
          <p style={{ marginTop: "8px", fontWeight: '700', fontSize: "13px", marginLeft: "8px", marginRight: "8px" }}>V2</p>
        </div>

      </ToolbarButtonContainer>
      {charger && (
        <>
          <ChargerDetailsTable>
            <tbody>
              <ChargerDetailsRow>
                <td style={{ display: 'flex', width: '20%' }}>
                  <Icon path="general.charger" width="48px" height="48px" marginleft="24px" marginbottom="16px" marginright="16px" margintop="16px" />
                  <div>
                    <ChargerDetailsTitle>{t('chargerLogs_chargerName')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.localName}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex', width: '20%' }}>
                  <Icon path="chargers.user" width="24px" height="24px" marginbottom="16px" marginright="16px" margintop="28px" />
                  <div>
                    <ChargerDetailsTitle>{t('chargerLogs_email')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.userEmail}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex', width: '15%' }}>
                  <div>
                    <ChargerDetailsTitle>{t('chargerLogs_chargerId')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.chargerId}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex', width: '15%' }}>
                  <div>
                    <ChargerDetailsTitle>{t('chargerLogs_serialNumber')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.serialNumber == ' ' ? '-' : charger.serialNumber || '-'}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex', width: '10%' }}>
                  <div>
                    <ChargerDetailsTitle>{t('chargers_swVersion')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.swVersion}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex', width: '10%' }}>
                  <div>
                    <ChargerDetailsTitle>{t('chargers_fwVersion')}</ChargerDetailsTitle>
                    <ChargerDetailsValue>{charger.fwVersion}</ChargerDetailsValue>
                  </div>
                </td>
                <td style={{ display: 'flex' }}>
                  <ChargerDetailsGoToChargerContainer>
                    <Navigator to={`/chargers/${charger.chargerId}`}><ChargerDetailsGoToChargerLabel>{t('chargerLogs_chargerDataSheet')}</ChargerDetailsGoToChargerLabel></Navigator>
                    <Icon path="general.forwardArrow" width="18px" height="18px" margintop="30px" color="primary" />
                  </ChargerDetailsGoToChargerContainer>
                </td>
              </ChargerDetailsRow>
            </tbody>
          </ChargerDetailsTable>
          <DateRangePickerContainer>
            <DateRangePickerWithPresets onSelect={(range) => handleChooseDateRange(range)} defaultDate={selectedDateRange} />
          </DateRangePickerContainer>
        </>
      )}
      <WhiteSpace height='16px' />
      {displayedLogs && displayedLogs.length > 0 ? <>
        <div style={{ position: 'relative' }}>
          <LogToolbar>
            <Icon path="general.filter" width="16px" height="16px" color={appliedLineFilter ? "primary" : undefined} onClick={appliedLineFilter ? clearLineFilter : openLineFilter} />
            <Icon path="general.copy" width="16px" height="16px" onClick={handleCopyLogsToClipboard} />
          </LogToolbar>
        </div>
        <Table key={appliedLineFilter ? `${appliedLineFilter.from}-${appliedLineFilter.to}` : 'all'} onSearchTermChange={setTableSearchTerm} id="ChargerLogs" height="calc(90vh - 235px)" isSearchEnabled={true} isSearchCtrlFEnabled={true} isPaginationEnabled={false} columns={[
          { width: '15px' },
          { label: t('chargerLogs_lineNumber'), width: '7%' },
          { label: t('chargerLogs_time'), width: '5%' },
          { width: '6%' },
          { label: t('chargerLogs_appTimestamp'), width: '6%' },
          { label: t('chargerLogs_type'), width: '8%' },
          { label: t('chargerLogs_message') }
        ]} data={displayedLogs.map(log => {
          const selectLine = () => handleSelectLogLine(log.lineNumber, log.day)
          return [
          {
            onClick: selectLine,
            custom: <LogIndicator id={`log-line-${log.lineNumber}`} $code={getLogCodeType(log.logCode)} />
          }, {
            onClick: selectLine,
            sortBy: parseInt(log.lineNumber),
            value: {
              type: TableCellTypes.Label,
              value: log.lineNumber
            }
          }, {
            onClick: selectLine,
            sortBy: `${new Date(`${log.day} ${log.time}`).valueOf()} ${parseInt(log.lineNumber)}`,
            value: {
              type: TableCellTypes.Label,
              value: log.day.replace(/-/g, '.'),
            }
          }, {
            onClick: selectLine,
            value: {
              type: TableCellTypes.Text,
              value: log.time
            }
          }, {
            onClick: selectLine,
            sortBy: log.appTs,
            value: {
              type: TableCellTypes.Text,
              value: (log.appTs || "-").toString()
            }
          }, {
            onClick: selectLine,
            sortBy: log.logCode,
            custom: <LogCode $code={getLogCodeType(log.logCode)}>{getLogCodeLabel(log.logCode)}</LogCode>
          }, {
            onClick: selectLine,
            sortBy: log.message,
            value: {
              type: TableCellTypes.Text,
              value: log.message.replace(/,/g, ', ')
            }
          }
        ]})} />
      </> : <>
        {isLoadingLogs ? <>
          <div style={{width: "100%", textAlign: "center", marginTop: "80px", fontSize: "18px", fontWeight: "700"}}>
            <LoaderDots />
          </div>
        </> : <>
          <p style={{width: "100%", textAlign: "center", marginTop: "80px", fontSize: "15px", fontWeight: "500"}}>
            {appliedLineFilter && logs.length > 0 ? "No logs match the current filter" : "No available logs"}
          </p>
        </>}
      </>}

      {isShowingLineFilter && (
        <Modal onEscape={() => setIsShowingLineFilter(false)}>
          <LineFilterModalContainer>
            <LineFilterModalTitle>{t('chargerLogs_filterByLineNumber')}</LineFilterModalTitle>
            <InputWithLabel
              autoFocus
              width="100%"
              label={t('chargerLogs_fromLine')}
              value={lineFilterFromInput}
              onInput={(value) => setLineFilterFromInput(value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyLineFilter() }}
            />
            <WhiteSpace height="16px" />
            <InputWithLabel
              width="100%"
              label={t('chargerLogs_toLine')}
              value={lineFilterToInput}
              onInput={(value) => setLineFilterToInput(value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') applyLineFilter() }}
            />
            <LineFilterModalButtonContainer>
              <Button onclick={applyLineFilter} label={t('chargerLogs_apply')} width="calc(50% - 8px)" bgcolor="#4AB1FC" labelcolor="white" />
              <WhiteSpace width="16px" />
              <Button onclick={() => setIsShowingLineFilter(false)} label={t('chargerLogs_cancel')} width="calc(50% - 8px)" />
            </LineFilterModalButtonContainer>
          </LineFilterModalContainer>
        </Modal>
      )}
    </>
  )
}